import { API_V1 } from '../config'
import { apiFetch } from './api'

export interface DocumentReminder {
  id: number
  reminder_type: 'expiry' | 'renewal' | 'review' | 'custom'
  remind_at: string
  days_before_expiry: number | null
  message: string
  status: 'pending' | 'sent' | 'failed'
  sent_at: string | null
  recurrence: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'
  recurrence_end_date: string | null
  retry_count: number
  last_error: string | null
  can_retry: boolean
  recipients: string[]
  include_responsible: boolean
  calendar_event_id: string | null
  calendar_provider: string | null
  created_at: string
}

export interface CreateReminderRequest {
  reminder_type: 'expiry' | 'renewal' | 'review' | 'custom'
  remind_at?: string
  days_before_expiry?: number
  message?: string
  recurrence?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'
  recurrence_end_date?: string
}

/** List reminders for a document */
export async function getReminders(koblinkId: string, actorId?: string): Promise<DocumentReminder[]> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(koblinkId)}/reminders/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(koblinkId)}/reminders/`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch reminders: ${response.status}`)
  }
  const data = await response.json()
  return data.results ?? data
}

/** Create a reminder for a document */
export async function createReminder(
  koblinkId: string,
  data: CreateReminderRequest,
  actorId?: string,
): Promise<DocumentReminder> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(koblinkId)}/reminders/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(koblinkId)}/reminders/`
  const response = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to create reminder: ${response.status}`)
  }
  return response.json()
}

/** Delete a reminder */
export async function deleteReminder(reminderId: number): Promise<void> {
  const response = await apiFetch(`${API_V1}/reminders/${reminderId}/`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    throw new Error(`Failed to delete reminder: ${response.status}`)
  }
}

/** Retry a failed reminder */
export async function retryReminder(reminderId: number): Promise<DocumentReminder> {
  const response = await apiFetch(`${API_V1}/reminders/${reminderId}/retry/`, {
    method: 'POST',
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to retry reminder: ${response.status}`)
  }
  return response.json()
}
